import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'store';
import HttpError from '@common/interfaces/api/http-error';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import localRobotDataService from 'services/data/local-robot-data-service';
import { selectSelectedRobotId } from 'store/robots/robots-slice-selectors';
import ParamList, {
  mapParamListApiToParamList,
  mapPartialParamListToParamListApi,
} from '@common/interfaces/robot/configurations/param-list';
import { EnvironmentPollution } from '@common/interfaces/robot/cleaning-configurations/environment-pollution';
import NavLocalPlanner from '@common/interfaces/configuration/nav/nav-local-planner';
import NavTasks from '@common/interfaces/configuration/nav/nav-tasks';
import DeepPartial from '@common/utils/deep-partial';
import ExcludedDate from '@common/interfaces/robot/excluded-dates/excluded-date';
import CleaningCommandButton, {
  mapButtonConfigurationApiDataToButtonList,
} from '@common/interfaces/robot/buttons/command-buttons';
import ValidationResult from '@common/interfaces/api/validation-result';

export const getParamList = createAsyncThunk<
  ParamList,
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/param-list/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    const apiData = await localRobotDataService.getParamList(robotId);
    return mapParamListApiToParamList(apiData);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchParamList = createAsyncThunk<
  ParamList,
  Partial<ParamList>,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/param-list/patch', async (paramList, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapPartialParamListToParamListApi(paramList);

  try {
    const apiData = await localRobotDataService.patchParamList(robotId, outgoingApiData);
    return mapParamListApiToParamList(apiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getEnvironmentPollution = createAsyncThunk<
  EnvironmentPollution[],
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/environment-pollution/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.getEnvironmentPollution(robotId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchCustomEnvironmentPollution = createAsyncThunk<
  EnvironmentPollution,
  Partial<EnvironmentPollution>,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>(
  'local-robot/environment-pollution/patch-custom',
  async (config, { getState, rejectWithValue }) => {
    const state = getState();
    const robotId = selectSelectedRobotId(state);

    if (robotId == null)
      return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

    try {
      return await localRobotDataService.patchCustomEnvironmentPollution(robotId, config);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const getNavLocalPlanner = createAsyncThunk<
  NavLocalPlanner,
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/nav/local-planner/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.getNavLocalPlanner(robotId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchNavLocalPlanner = createAsyncThunk<
  NavLocalPlanner,
  DeepPartial<NavLocalPlanner>,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/nav/local-planner/patch', async (navLocalPlanner, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.patchNavLocalPlanner(robotId, navLocalPlanner);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getNavTasks = createAsyncThunk<
  NavTasks,
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/nav/tasks/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.getNavTasks(robotId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchNavTasks = createAsyncThunk<
  NavTasks,
  DeepPartial<NavTasks>,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/nav/tasks/patch', async (navTasks, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.patchNavTasks(robotId, navTasks);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getExcludedDates = createAsyncThunk<
  ExcludedDate[],
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/excluded-dates/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.getExcludedDates(robotId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postExcludedDate = createAsyncThunk<
  ExcludedDate,
  ExcludedDate,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/excluded-dates/post', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.postExcludedDate(robotId, payload);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchExcludedDateById = createAsyncThunk<
  ExcludedDate,
  { id: string; data: Partial<ExcludedDate> },
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/excluded-dates/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.patchExcludedDateById(robotId, payload.id, payload.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteExcludedDateById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: HttpError | ValidationResult[] }
>('local-robot/excluded-dates/delete', async (id, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteExcludedDateById(robotId, id);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getButtonConfiguration = createAsyncThunk<
  CleaningCommandButton[],
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/button-configuration/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    const apiData = await localRobotDataService.getButtonConfiguration(robotId);
    return mapButtonConfigurationApiDataToButtonList(apiData);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getRobotApiConfigs = createAsyncThunk<void, void>(
  'local-robot/api-configurations/get',
  async () => {
    console.error('getRobotApiConfigs is not implemented for sphere... we should do that.');
  },
);
